/**
 * Grouped Product Card — Variant-as-Product (Optimized)
 * Loaded globally via layout/theme.liquid (defer).
 * Handles color swatch clicks + size select dropdown changes — no fetch needed.
 * 
 * Key Features:
 * - Preserves size selection when changing colors
 * - Updates badges dynamically based on selected variant
 * - Syncs Quick Buy button with selected variant
 * - Optimized DOM queries with caching
 * - Proper state management
 */

class GroupedProductCard extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    
    // Wait for children to be parsed/appended (essential for AJAX-loaded contents like recommendations/recent products)
    setTimeout(() => {
      if (this._initialized) return;
      this._initialized = true;
      this._initCard();
    }, 0);
  }

  _initCard() {
    // Parse siblings data
    try {
      this._siblings = JSON.parse(this.dataset.siblings || '[]');
    } catch (e) {
      console.warn('[GPC] Could not parse siblings JSON', e);
      this._siblings = [];
    }

    // Initialize state
    this._activeColor = this.dataset.currentColor || '';
    this._activeSize  = this.dataset.currentSize  || '';
    this._currentVariant = null;
    this._userHasSelectedSize = false;

    // Cache DOM elements for performance
    this._cacheElements();
    
    // Build variant lookup map for O(1) access
    this._buildVariantMap();
    
    // Set initial variant
    this._setInitialVariant();
    
    // Bind events
    this._bindEvents();

    // Force the size dropdown selection to reset/align with the default active size on page load
    const sizeSelect = this.querySelector('[data-size-select]');
    if (sizeSelect && this._activeSize) {
      sizeSelect.value = this._activeSize;
      if (sizeSelect.selectedIndex === -1) {
        const normTarget = this._normalizeSize(this._activeSize);
        for (let i = 0; i < sizeSelect.options.length; i++) {
          if (this._normalizeSize(sizeSelect.options[i].value) === normTarget) {
            sizeSelect.selectedIndex = i;
            break;
          }
        }
      }
    }

    // Initial color filtering based on active size
    this._filterColors();
    this._updateSizeDropdownOptions();
  }

  // ─── Cache DOM elements ──────────────────────────────────────────────────────

  _cacheElements() {
    this._els = {
      image: this.querySelector('[data-grouped-image]'),
      price: this.querySelector('[data-grouped-price]'),
      cardLinks: this.querySelectorAll('[data-card-link]'),
      quickBuyBtn: this.querySelector('[data-button-quick-view]'),
      badgeContainer: this.querySelector('.product-badge'),
      sizeContainer: this.querySelector('[data-grouped-sizes]'),
      swatchContainer: this.querySelector('[data-grouped-swatches]')
    };
  }

  // ─── Build variant lookup map ────────────────────────────────────────────────

  _buildVariantMap() {
    // Create fast lookup: variantMap[color][size] = variant
    this._variantMap = {};
    this._siblings.forEach(sib => {
      const color = sib.color || '';
      const size = sib.size || '';
      
      if (!this._variantMap[color]) {
        this._variantMap[color] = {};
      }
      
      // Store by size, or as default if no size
      if (size) {
        this._variantMap[color][size] = sib;
      } else {
        this._variantMap[color]['_default'] = sib;
      }
    });
  }

  // ─── Normalize size formatting ───────────────────────────────────────────────

  _normalizeSize(size) {
    return (size || '').toLowerCase().replace(/[\s-_]/g, '');
  }

  _normalizeColor(color) {
    return (color || '').trim().toLowerCase();
  }

  // ─── Set initial variant ─────────────────────────────────────────────────────

  _setInitialVariant() {
    this._currentVariant = this._siblings.find(
      s => this._normalizeColor(s.color) === this._normalizeColor(this._activeColor) && this._normalizeSize(s.size) === this._normalizeSize(this._activeSize)
    ) || this._siblings.find(
      s => this._normalizeColor(s.color) === this._normalizeColor(this._activeColor)
    ) || this._siblings[0];

    if (this._currentVariant) {
      this._updateQuickBuyVariant(this._currentVariant);
      this._updateBadges(this._currentVariant);
    }
  }

  // ─── Bind events ─────────────────────────────────────────────────────────────

  _bindEvents() {
    // Use event delegation for swatches
    if (this._els.swatchContainer) {
      this._els.swatchContainer.addEventListener('click', e => {
        const btn = e.target.closest('[data-color-swatch]');
        if (btn) {
          e.preventDefault();
          if (btn.classList.contains('is-disabled')) return;
          this._onColorClick(btn);
        }
      });
    }

    // Size select change
    const sizeSelect = this.querySelector('[data-size-select]');
    if (sizeSelect) {
      sizeSelect.addEventListener('change', () => this._onSizeSelect(sizeSelect));
    }
  }

  // ─── Color swatch click ──────────────────────────────────────────────────────

  _onColorClick(btn) {
    const color = btn.dataset.color;
    if (!color || this._normalizeColor(color) === this._normalizeColor(this._activeColor)) return;

    this._activeColor = color;
    this.dataset.currentColor = color;

    // Find variant for this color and active size
    const colorNorm = this._normalizeColor(color);
    const hasSizeSelect = !!this.querySelector('[data-size-select]');
    let variant = this._siblings.find(
      s => this._normalizeColor(s.color) === colorNorm && (!hasSizeSelect || this._normalizeSize(s.size) === this._normalizeSize(this._activeSize))
    );

    // Fallback: if no variant matches the active size, select the first variant for this color
    if (!variant) {
      variant = this._siblings.find(s => this._normalizeColor(s.color) === colorNorm);
    }

    if (!variant) return;

    this._currentVariant = variant;
    this._activeSize = variant.size;
    this.dataset.currentSize = variant.size;

    // Update size select dropdown value if it exists
    const sizeSelect = this.querySelector('[data-size-select]');
    if (sizeSelect) {
      sizeSelect.value = variant.size;
      // If exact match didn't select anything, try normalized match
      if (sizeSelect.selectedIndex === -1 && variant.size) {
        const normTarget = this._normalizeSize(variant.size);
        for (let i = 0; i < sizeSelect.options.length; i++) {
          if (this._normalizeSize(sizeSelect.options[i].value) === normTarget) {
            sizeSelect.selectedIndex = i;
            break;
          }
        }
      }
    }

    // Update UI
    this._updateImage(variant.image);
    this._updatePrice(variant.price_html);
    this._updateLinks(variant.url);
    this._updateBadges(variant);
    this._updateQuickBuyVariant(variant);

    // Update active swatch class
    this.querySelectorAll('[data-color-swatch]').forEach(s => s.classList.remove('is-active'));
    btn.classList.add('is-active');

    // Update data attributes
    this.dataset.currentUrl = variant.url;

    // If more link exists, update its link to this variant
    const moreLink = this.querySelector('[data-swatch-more-link]');
    if (moreLink) {
      moreLink.setAttribute('href', variant.url);
    }

    // Update all swatch availability states based on the new size
    this._filterColors();
  }

  // ─── Size select change ───────────────────────────────────────────────────────

  _onSizeSelect(select) {
    const option = select.options[select.selectedIndex];
    if (!option) return;

    this._userHasSelectedSize = true;

    const size = option.value;
    this._activeSize = size;
    this.dataset.currentSize = size;

    // Check if currently active color has an available variant in the new size
    const activeColorNorm = this._normalizeColor(this._activeColor);
    const activeColorVariant = this._siblings.find(
      s => this._normalizeColor(s.color) === activeColorNorm &&
           this._normalizeSize(s.size) === this._normalizeSize(size)
    );

    // If active color is missing or out of stock, default to the first available in-stock color in this size
    if (!activeColorVariant || !activeColorVariant.available) {
      const firstAvailableInSize = this._siblings.find(
        s => this._normalizeSize(s.size) === this._normalizeSize(size) && s.available
      );
      if (firstAvailableInSize && firstAvailableInSize.color) {
        this._activeColor = firstAvailableInSize.color;
        this.dataset.currentColor = firstAvailableInSize.color;
      }
    }

    // Filter colors and update UI
    this._filterColors();
    this._updateSizeDropdownOptions();
  }

  // ─── Filter colors by active size ─────────────────────────────────────────────

  _filterColors() {
    const swatches = this.querySelectorAll('[data-color-swatch]');
    const moreLink = this.querySelector('[data-swatch-more-link]');
    const hasSizeSelect = !!this.querySelector('[data-size-select]');

    const swatchesWithVariantInSize = [];
    const availableSwatches = [];
    const unavailableSwatches = [];
    let activeSwatch = null;

    if (swatches.length > 0) {
      swatches.forEach((swatch, index) => {
        const color = swatch.dataset.color;
        const colorNorm = this._normalizeColor(color);
        const variants = this._siblings.filter(s => this._normalizeColor(s.color) === colorNorm);
        const matchedVariant = variants.find(
          v => !hasSizeSelect || this._normalizeSize(v.size) === this._normalizeSize(this._activeSize)
        );

        // Check availability and update classes
        if (matchedVariant) {
          swatchesWithVariantInSize.push(swatch);
          if (matchedVariant.available) {
            swatch.classList.remove('is-unavailable');
          } else {
            swatch.classList.add('is-unavailable');
          }

          // Dynamically update the swatch thumbnail image to match the currently selected size
          const label = swatch.querySelector('.swatch__label');
          if (label && matchedVariant.thumb) {
            label.style.backgroundImage = `url('${matchedVariant.thumb}')`;
          }
        } else {
          // No variant exists for this color in the active size
          swatch.classList.add('is-unavailable');
        }
        
        // Ensure we remove is-disabled so the button is always clickable
        swatch.classList.remove('is-disabled');
      });

      // Auto-switch active color if currently selected color doesn't exist at all in the new size
      activeSwatch = swatchesWithVariantInSize.find(s => this._normalizeColor(s.dataset.color) === this._normalizeColor(this._activeColor));
      if (!activeSwatch && swatchesWithVariantInSize.length > 0) {
        activeSwatch = swatchesWithVariantInSize[0];
        this._activeColor = activeSwatch.dataset.color;
        this.dataset.currentColor = this._activeColor;
      }

      // Update active swatch class and visibility
      swatches.forEach((swatch) => {
        const color = swatch.dataset.color;
        const colorNorm = this._normalizeColor(color);
        const variants = this._siblings.filter(s => this._normalizeColor(s.color) === colorNorm);
        const matchedVariant = variants.find(
          v => !hasSizeSelect || this._normalizeSize(v.size) === this._normalizeSize(this._activeSize)
        );
        if (matchedVariant) {
          availableSwatches.push(swatch);
        } else {
          unavailableSwatches.push(swatch);
        }
      });

      const visibleSwatches = [];
      if (!this._userHasSelectedSize) {
        // Default state: show first 4 swatches and the active one (hide index 0 if active index is >= 4)
        const activeIndex = Array.from(swatches).indexOf(activeSwatch);
        swatches.forEach((swatch, index) => {
          let shouldShow = false;
          const isCurrentActive = swatch === activeSwatch;
          if (isCurrentActive) {
            shouldShow = true;
          } else if (index < 4) {
            if (index === 0) {
              shouldShow = activeIndex < 4;
            } else {
              shouldShow = true;
            }
          }
          if (shouldShow) {
            visibleSwatches.push(swatch);
          }
        });
      } else {
        // User has selected a size/style: ONLY show colors available in this size/style (up to 4)
        if (activeSwatch && availableSwatches.includes(activeSwatch)) {
          visibleSwatches.push(activeSwatch);
        }
        availableSwatches.forEach((swatch) => {
          if (visibleSwatches.length < 4 && !visibleSwatches.includes(swatch)) {
            visibleSwatches.push(swatch);
          }
        });
      }

      swatches.forEach((swatch) => {
        const isCurrentActive = this._normalizeColor(swatch.dataset.color) === this._normalizeColor(this._activeColor);
        
        if (isCurrentActive) {
          swatch.classList.add('is-active');
        } else {
          swatch.classList.remove('is-active');
        }

        if (swatches.length > 5) {
          if (visibleSwatches.includes(swatch)) {
            swatch.style.display = '';
          } else {
            swatch.style.display = 'none';
          }
        } else {
          // If user has selected size, hide it if it's not available in that size
          if (this._userHasSelectedSize) {
            if (availableSwatches.includes(swatch)) {
              swatch.style.display = '';
            } else {
              swatch.style.display = 'none';
            }
          } else {
            swatch.style.display = '';
          }
        }
      });
    }

    // Find variant for active color (or empty if no color) and active size
    const variant = this._siblings.find(
      s => this._normalizeColor(s.color) === this._normalizeColor(this._activeColor) && (!hasSizeSelect || this._normalizeSize(s.size) === this._normalizeSize(this._activeSize))
    ) || this._siblings.find(
      s => !hasSizeSelect || this._normalizeSize(s.size) === this._normalizeSize(this._activeSize)
    );

    if (variant) {
      this._currentVariant = variant;
      this._updateImage(variant.image);
      this._updatePrice(variant.price_html);
      this._updateLinks(variant.url);
      this._updateBadges(variant);
      this._updateQuickBuyVariant(variant);
      this.dataset.currentUrl = variant.url;

      // If more link exists, update its link to this variant
      if (moreLink) {
        moreLink.setAttribute('href', variant.url);
      }
    }

    if (moreLink) {
      if (this._userHasSelectedSize) {
        moreLink.style.display = availableSwatches.length > 4 ? '' : 'none';
      } else {
        moreLink.style.display = swatches.length > 5 ? '' : 'none';
      }
    }

    // Always update meta text (Size/Style selected)
    this._updateMetaText();
  }

  // ─── Update image ────────────────────────────────────────────────────────────

  _updateImage(src) {
    if (!src || !this._els.image) return;
    
    const img = this._els.image.tagName === 'IMG' 
      ? this._els.image 
      : this._els.image.querySelector('img');
    
    if (!img) return;

    // Smooth transition
    img.style.transition = 'opacity 0.2s ease';
    img.style.opacity = '0';
    
    const newSrc = src;
    
    // Only update if different
    if (img.src === newSrc) {
      img.style.opacity = '1';
      return;
    }
    
    img.onload = () => {
      img.style.opacity = '1';
    };
    img.onerror = () => {
      img.style.opacity = '1';
    };
    
    img.src = newSrc;
    img.srcset = '';
  }

  // ─── Update price ────────────────────────────────────────────────────────────

  _updatePrice(priceHtml) {
    if (!this._els.price || !priceHtml) return;
    
    this._els.price.classList.add('is-updating');
    
    requestAnimationFrame(() => {
      setTimeout(() => {
        this._els.price.innerHTML = priceHtml;
        this._els.price.classList.remove('is-updating');
      }, 150);
    });
  }

  // ─── Update links ────────────────────────────────────────────────────────────

  _updateLinks(url) {
    if (!url) return;
    this._els.cardLinks.forEach(link => link.setAttribute('href', url));
  }

  // ─── Update badges (Critical Fix) ────────────────────────────────────────────

  _updateBadges(variant) {
    if (!this._els.badgeContainer) return;

    const soldOutBadge = this._els.badgeContainer.querySelector('.product-badge__item--sold');
    const saleBadge = this._els.badgeContainer.querySelector('.product-badge__item--sale');
    let savingBadge = this._els.badgeContainer.querySelector('.product-badge__item--saving');

    // Update sold out badge based on variant availability
    if (soldOutBadge) {
      soldOutBadge.style.display = variant.available ? 'none' : '';
    }

    const hasComparePrice = variant.compare_price && variant.compare_price > variant.price;

    if (hasComparePrice) {
      const diff = variant.compare_price - variant.price;
      const percent = Math.floor((diff / variant.compare_price) * 100);

      if (savingBadge) {
        const span = savingBadge.querySelector('span');
        if (span) {
          span.textContent = `Save ${percent}%`;
        }
        savingBadge.style.display = '';
        if (saleBadge) saleBadge.style.display = 'none';
      } else if (saleBadge) {
        saleBadge.style.display = '';
      }
    } else {
      if (savingBadge) savingBadge.style.display = 'none';
      if (saleBadge) saleBadge.style.display = 'none';
    }

    // Update soldout class on card
    this.classList.toggle('soldout', !variant.available);
  }

  // ─── Update Quick Buy variant (Critical Fix) ─────────────────────────────────

  _updateQuickBuyVariant(variant) {
    if (!this._els.quickBuyBtn || !variant) return;

    // Set variant ID for quick view to open with correct variant
    this._els.quickBuyBtn.setAttribute('data-variant-id', variant.id);
    
    // Update handle
    this._els.quickBuyBtn.setAttribute('data-handle', variant.handle);
    
    // Disable if not available
    if (variant.available) {
      this._els.quickBuyBtn.removeAttribute('disabled');
    } else {
      this._els.quickBuyBtn.setAttribute('disabled', 'disabled');
    }
  }

  // ─── Update Meta Text ────────────────────────────────────────────────────────

  _updateMetaText() {
    const metaVal = this.querySelector('[data-gpc-meta-value]');
    if (metaVal) {
      metaVal.textContent = this._activeSize;
    }
  }
}

if (!customElements.get('marmeto-product-card')) {
  customElements.define('marmeto-product-card', GroupedProductCard);
}